import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { Email, Send, CheckCircle, Info } from '@mui/icons-material';
import { invoiceService } from '../services/invoiceService';

interface InvoiceEmailDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  invoiceNumber: string;
  defaultEmail?: string;
}

const InvoiceEmailDialog: React.FC<InvoiceEmailDialogProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceNumber,
  defaultEmail = ''
}) => {
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState(`Invoice #${invoiceNumber} - C-Vision Attendance System`);
  const [message, setMessage] = useState(`Dear Customer,

Please find attached the invoice #${invoiceNumber} for your C-Vision Attendance System subscription.

The invoice includes:
• Professional PDF design with enhanced visual elements
• QR code for easy mobile payments
• Complete payment information and bank details
• Detailed breakdown of services and charges

Payment Methods:
• Bank Transfer
• M-Pesa
• Credit/Debit Card

If you have any questions, please don't hesitate to contact us.

Best regards,
C-Vision Team`);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSendEmail = async () => {
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await invoiceService.sendEmail(invoiceId, {
        to_email: email,
        subject: subject,
        message: message
      });
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Email color="primary" />
          <Typography variant="h6">Send Invoice Email</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3}>
          <Alert severity="info" icon={<Info />}>
            <Typography variant="body2">
              Send a professional email with the enhanced PDF invoice attached.
            </Typography>
          </Alert>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Enhanced PDF Features Included
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1" minWidth="250px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Professional design & branding</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">QR code for mobile payments</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Status badges with colors</Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="250px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Enhanced typography & layout</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Complete payment information</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Professional footer</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            margin="normal"
            multiline
            rows={8}
          />
        </Box>

        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8fafc' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Invoice:</strong> #{invoiceNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The email will include the enhanced PDF invoice as an attachment.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Email sent successfully!
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSendEmail}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceEmailDialog; 